import {
  get_raw_owner_role,
  normalize_role_name,
  read_safe_object_value
} from './my_queue_helpers';

export function dispatch_system_loader(message) {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
}

export function clear_system_loader() {
  window.dispatchEvent(new CustomEvent('system-loader:hide'));
}

function normalize_text_value(value = '') {
  const normalized_value = String(value || '').trim();
  return normalized_value;
}

function first_text_value(values = []) {
  const matched_value = values.find((value) => normalize_text_value(value));
  return normalize_text_value(matched_value);
}

function first_defined_value(values = []) {
  const matched_value = values.find((value) => value !== undefined && value !== null && value !== '');
  return matched_value;
}

function copy_string_array(value = []) {
  if (!Array.isArray(value)) return [];
  const copied_values = value.map((item) => String(item)).filter(Boolean);
  return copied_values;
}

function owner_role_id_candidates(role = {}, raw_role = {}) {
  const candidates = [
    raw_role?.id,
    raw_role?.role_id,
    role?.role_id,
    role?.id
  ];
  return candidates;
}

export function build_normalized_owner_role(role = {}) {
  const raw_role = get_raw_owner_role(role);
  const name = normalize_role_name(role, raw_role);
  const role_id = first_defined_value(owner_role_id_candidates(role, raw_role));
  const normalized_role = {
    id: role_id,
    name: name || 'Role'
  };
  return normalized_role;
}

export function parse_persisted_case_edits(raw_value = '') {
  const persisted_edits = raw_value ? JSON.parse(raw_value) : {};
  return persisted_edits;
}

export function find_email_in_direct_keys(source = {}, direct_keys = []) {
  let resolved_email = '';
  for (const key of direct_keys) {
    const value = String(read_safe_object_value(source, key, '') || '').trim();
    if (value.includes('@')) {
      resolved_email = value;
      break;
    }
  }
  return resolved_email;
}

export function find_email_in_string_entries(source = {}) {
  let resolved_email = '';
  for (const [key, value] of Object.entries(source)) {
    if (is_email_string_entry(key, value)) {
      resolved_email = value.trim();
      break;
    }
  }
  return resolved_email;
}

function is_email_string_entry(key = '', value = '') {
  const key_names_email = String(key || '').toLowerCase().includes('email');
  const value_has_email = typeof value === 'string' && value.trim().includes('@');
  return key_names_email && value_has_email;
}

function resolve_nested_email_value(vm, value, seen) {
  if (Array.isArray(value)) return find_email_in_nested_values(vm, value, seen);
  if (value && typeof value === 'object') return vm.resolve_email_value(value, seen);
  return '';
}

export function find_email_in_nested_values(vm, values = [], seen = new Set()) {
  let resolved_email = '';
  for (const value of values) {
    resolved_email = resolve_nested_email_value(vm, value, seen);
    if (resolved_email) break;
  }
  return resolved_email;
}

function build_next_case_template_fields(base_case = {}, edit_form = {}) {
  const template_name = first_text_value([
    edit_form.template_name,
    base_case?.template_name
  ]);
  const fields = {
    template_name: template_name,
    template: first_text_value([edit_form.template_name, base_case?.template]),
    template_ids: copy_string_array(edit_form.template_ids)
  };
  return fields;
}

function build_next_case_email_fields(email = '') {
  const fields = {
    email: email,
    contact_email: email,
    user_email: email,
    applicant_email: email,
    candidate_email: email
  };
  return fields;
}

export function build_next_case_from_edit_form(base_case = {}, edit_form = {}, owner_role_name = '', activate_by = '') {
  const normalized_email = normalize_text_value(edit_form.email);
  const next_case = {
    ...base_case,
    name: normalize_text_value(edit_form.onboarding_name),
    journey: first_text_value([edit_form.journey, base_case?.journey]),
    ...build_next_case_template_fields(base_case, edit_form),
    onboarding_owner_role_name: owner_role_name,
    owner_role_ids: copy_string_array(edit_form.owner_role_ids),
    onboarding_activate_by: activate_by,
    ...build_next_case_email_fields(normalized_email)
  };
  return next_case;
}

export function build_save_case_payload(application_id, owner_role_id, activate_by) {
  const payload = {
    application_id: application_id,
    owner_role_id: owner_role_id ? Number(owner_role_id) : null,
    activate_by: activate_by || null
  };
  return payload;
}

export function normalize_edit_date(value = '') {
  const raw_value = normalize_text_value(value);
  if (is_blank_edit_date(raw_value)) return '';
  return normalize_iso_edit_date(raw_value) || normalize_slash_edit_date(raw_value);
}

function is_blank_edit_date(value = '') {
  return !value || value === '-';
}

function normalize_iso_edit_date(value = '') {
  const iso_match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso_match) return iso_match[0];
  return '';
}

function normalize_slash_edit_date(value = '') {
  const slash_match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!slash_match) return '';
  const day = slash_match[1].padStart(2, '0');
  const month = slash_match[2].padStart(2, '0');
  return `${slash_match[3]}-${month}-${day}`;
}

export function unique_named_options(options = []) {
  const seen_names = new Set();
  return options.filter((option) => {
    const name = String(option?.name || '').trim();
    if (!name || seen_names.has(name.toLowerCase())) return false;
    seen_names.add(name.toLowerCase());
    return true;
  });
}

export function build_current_option(name = '', id = '') {
  const option_name = String(name || '').trim();
  if (!option_name) return null;
  return { id: id || option_name, name: option_name };
}

export function split_template_names(value = '') {
  return String(value || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
}

export function build_template_label(templates = []) {
  if (!templates.length) return '';
  if (templates.length === 1) return templates[0].name;
  return `${templates.length} templates selected`;
}

export function build_template_names(templates = []) {
  return templates.map((template) => template?.name).filter(Boolean).join(', ');
}

export function build_option_label(options = [], singular_label = '') {
  if (!options.length) return '';
  if (options.length === 1) return options[0].name || singular_label;
  return `${options.length} ${singular_label}s selected`;
}

export function build_toggled_id_list(selected_ids = [], next_id = '') {
  const toggled_ids = new Set(selected_ids);
  if (toggled_ids.has(next_id)) {
    toggled_ids.delete(next_id);
  } else {
    toggled_ids.add(next_id);
  }
  return Array.from(toggled_ids);
}

export function build_owner_role_names(roles = []) {
  const role_names = roles.map((role) => role.name).filter(Boolean).join(', ');
  return role_names;
}

export function find_option_id_by_name(options = [], name = '') {
  const normalized_name = String(name || '').trim().toLowerCase();
  const option = options.find((item) => String(item?.name || '').trim().toLowerCase() === normalized_name);
  return option?.id ? String(option.id) : '';
}

export function resolve_case_template_name(case_item = {}) {
  if (Array.isArray(case_item?.onboarding_template_names)) {
    return case_item.onboarding_template_names.map((name) => String(name || '').trim()).filter(Boolean).join(', ');
  }
  return String(case_item?.template_name || case_item?.template || '').trim();
}

export function resolve_case_template_ids(case_item = {}) {
  return Array.isArray(case_item?.template_ids)
    ? case_item.template_ids.map((template_id) => String(template_id)).filter(Boolean)
    : [];
}

export function resolve_case_owner_role_ids(case_item = {}) {
  return Array.isArray(case_item?.owner_role_ids)
    ? case_item.owner_role_ids.map((role_id) => String(role_id)).filter(Boolean)
    : [];
}
